import { target } from "@github/catalyst";
import { controller } from "./controller";
import { loggingFetch } from "../utils/loggingFetch";
import { envConfig } from "../config/envConfigs";
import { authInfo } from "../utils/authInfo";
import { debounce } from "@github/mini-throttle/dist/decorators";

type Address = {
  fullStreetAddress: string;
};

const fetchAddresses = async (address: string): Promise<Address[]> => {
  const data = await loggingFetch(`${envConfig.urls.typeahead}?address=${address}`, {
    headers: {
      Authorization: `Bearer ${(await authInfo).accessToken}`,
    },
  });
  if (data.ok) {
    return (await data.json()).addresses as Address[];
  }
  return [];
};

const buildOptions = (addresses: Address[]): HTMLOptionElement[] =>
  addresses.map((address) => {
    const option = document.createElement("option");
    option.textContent = address.fullStreetAddress;
    return option;
  });

@controller
export default class AddressTypeaheadElement extends HTMLElement {
  @target input: HTMLInputElement | undefined;
  @target datalist: HTMLDataListElement | undefined;
  addressInputLimit = 4;

  connectedCallback(): void {
    if (this.input && this.datalist) {
      this.input.addEventListener("input", this.handleInput.bind(this));
    }
  }

  @debounce(500)
  async handleInput(): Promise<void> {
    if (this.input?.value && this.datalist && this.input?.value?.trim().length >= this.addressInputLimit) {
      const addresses = await fetchAddresses(this.input.value);
      while (this.datalist?.firstChild) {
        this.datalist.removeChild(this.datalist.firstChild);
      }
      this.datalist?.append(...buildOptions(addresses));
    }
  }
}

declare global {
  interface Window {
    AddressTypeaheadElement: typeof AddressTypeaheadElement;
  }
  interface HTMLElementTagNameMap {
    "address-typeahead": AddressTypeaheadElement;
  }
}

if (!window.customElements.get("address-typeahead")) {
  window.AddressTypeaheadElement = AddressTypeaheadElement;
  window.customElements.define("address-typeahead", AddressTypeaheadElement);
}
